import { FileViewer } from "@/components/features/matter/file-repo/FileViewer";
import {
  Badge,
  Button,
  DateFormField,
  Form,
  Input,
  Typography,
} from "@/components/ui";
import { TextFormField } from "@/components/ui/FormFields/TextFormField/TextFormField";
import { useFulfillFirmSignatureRequest } from "@/lib/data/react-query/firm/firm-signature-request";
import { useFulfillSignatureRequest } from "@/lib/data/react-query/matters/matter-signature-request";
import { getFirmSignatureRequestPublicFileUrlV2 } from "@/lib/data/requests/firm/firm-signature-request";
import { getMatterSignatureRequestPublicFileUrlV2 } from "@/lib/data/requests/matters/matter-signature-request";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError } from "@/lib/types";
import { getFileNameParts } from "@/lib/utils/matterFile";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { AxiosError } from "axios";
import { startCase } from "lodash";
import { ArrowLeft, CheckCircle, XCircle } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";
import { LiveSignatureRequestFieldsView } from "./LiveSignatureRequestFieldsView";
import { DemoSignatureRequestView } from "./DemoSignatureRequestView";
import { type SignaturePortalViewModel } from "./signatureRequestPortalModel";

interface SignatureRequestViewProps {
  viewModel: SignaturePortalViewModel;
  scope: "matter" | "firm";
  onBack: () => void;
  passcode: string;
}

type SignerVariable = {
  name: string;
  label: string;
  type: "date" | "signature" | "text";
  dateFormat?: "default" | "short" | "long";
};

function getSignatureRequestErrorMessage(error: unknown) {
  if (!(error instanceof AxiosError)) {
    return "";
  }

  const typedError = error as AxiosError<APIError>;
  const logMsg = typedError.response?.data.error.logMsg ?? "";
  const userMsg = typedError.response?.data.meta.userMsg ?? "";
  return `${logMsg} ${userMsg}`.toLowerCase();
}

const buildSignatureFormSchema = (variables: SignerVariable[]) => {
  if (variables.length === 0) {
    return z.object({}).passthrough();
  }

  const shape: Record<string, z.ZodTypeAny> = {};

  variables.forEach((variable) => {
    shape[variable.name] = z
      .string()
      .trim()
      .min(1, { message: `${variable.label} is required` });
  });

  return z.object(shape);
};

export function SignatureRequestView({
  viewModel: data,
  scope,
  onBack,
  passcode,
}: SignatureRequestViewProps) {
  const fulfillMatterSignatureRequest = useFulfillSignatureRequest();
  const fulfillFirmSignatureRequest = useFulfillFirmSignatureRequest();
  const queryClient = useQueryClient();
  const [signedFilePresignedUrlKey, setSignedFilePresignedUrlKey] = useState<
    string | null
  >(null);
  const documentKey = signedFilePresignedUrlKey ?? data.filePresignedUrlKey;
  const signatureRequestDocumentUrlQuery = useQuery({
    queryKey: [
      queryKeys.signatureRequestPortal,
      "document",
      scope,
      passcode,
      documentKey,
    ],
    queryFn: () => {
      const requestData = {
        clientMethod: "get_object" as const,
        key: documentKey,
        signatureRequestPasscode: passcode,
      };

      return scope === "matter"
        ? getMatterSignatureRequestPublicFileUrlV2(requestData)
        : getFirmSignatureRequestPublicFileUrlV2(requestData);
    },
    enabled: !data.demoMode && Boolean(passcode) && Boolean(documentKey),
  });
  const signatureRequestDocumentUrl =
    signatureRequestDocumentUrlQuery.data?.data.presignedUrl;
  const signedDocumentGeneratorRef = useRef<(() => Promise<Blob>) | null>(null);
  const [isSigningDocument, setIsSigningDocument] = useState(false);
  const isFulfillmentPending =
    scope === "matter"
      ? fulfillMatterSignatureRequest.isPending
      : fulfillFirmSignatureRequest.isPending;
  const isSignSubmitPending = isFulfillmentPending || isSigningDocument;

  const requestingContact = data.requestingContact;
  const requestingUser = data.requestingUser;
  const rawSignerLabel =
    requestingUser?.label ?? requestingContact?.label ?? "Signer";

  const signerLabel = useMemo<string>(() => {
    if (!rawSignerLabel) {
      return "Signer1";
    }
    if (rawSignerLabel === "Signer") {
      return "Signer1";
    }
    if (rawSignerLabel === "UserSigner") {
      return "UserSigner1";
    }
    return rawSignerLabel;
  }, [rawSignerLabel]);

  const signerPrefixes = useMemo(() => {
    const prefixes = new Set<string>();
    prefixes.add(`${signerLabel}_`);
    if (signerLabel.startsWith("Signer")) {
      prefixes.add("Signer_");
    }
    if (signerLabel.startsWith("UserSigner")) {
      prefixes.add("UserSigner_");
    }
    return Array.from(prefixes);
  }, [signerLabel]);

  const signerVariables = useMemo<SignerVariable[]>(() => {
    const variables: SignerVariable[] = [];

    data.fileVariables.forEach((variable) => {
      const matchedPrefix = signerPrefixes.find((prefix) =>
        variable.startsWith(prefix),
      );

      if (!matchedPrefix) {
        return;
      }

      const rawName = variable.slice(matchedPrefix.length);
      const normalizedRawName = rawName.replace(/[_\s]+/g, " ").trim();
      const fallbackName = normalizedRawName.length
        ? normalizedRawName
        : variable.replace(/[_\s]+/g, " ").trim();
      const parts = rawName
        .split("_")
        .map((part) => part.trim())
        .filter((part) => part.length > 0);

      let type: SignerVariable["type"] = "text";
      let labelParts = parts;
      let dateFormat: SignerVariable["dateFormat"] = "default";

      if (parts.length > 0) {
        const candidateType = parts[0].toLowerCase();

        if (
          candidateType === "date" ||
          candidateType === "dateshort" ||
          candidateType === "datelong"
        ) {
          type = "date";
          dateFormat =
            candidateType === "dateshort"
              ? "short"
              : candidateType === "datelong"
                ? "long"
                : "default";
          labelParts = parts.slice(1);
        } else if (candidateType === "signature") {
          type = "signature";
          labelParts = parts.slice(1);
        } else if (candidateType === "text") {
          labelParts = parts.slice(1);
        } else {
          labelParts = parts;
        }
      }

      if (labelParts.length === 0) {
        const fallbackParts = fallbackName
          .split(" ")
          .map((part) => part.trim())
          .filter((part) => {
            if (!part.length) return false;
            const lower = part.toLowerCase();
            return ![
              "date",
              "dateshort",
              "datelong",
              "text",
              "signature",
            ].includes(lower);
          });

        if (fallbackParts.length > 0) {
          labelParts = fallbackParts;
        } else if (type === "signature") {
          labelParts = ["Signature"];
        } else if (type === "date") {
          labelParts = ["Date"];
        } else {
          labelParts = ["Field"];
        }
      }

      const label = startCase(labelParts.join(" "));

      if (type === "text") {
        const lowerCaseFallback = fallbackName.toLowerCase();
        if (lowerCaseFallback.includes("date")) {
          type = "date";
          dateFormat = "default";
        } else if (
          /^(?:Signer|UserSigner)(?:\d+)?_signature$/i.test(variable.trim())
        ) {
          type = "signature";
        }
      }

      variables.push({
        name: variable,
        label,
        type,
        dateFormat: type === "date" ? dateFormat : undefined,
      });
    });

    return variables;
  }, [data.fileVariables, signerPrefixes]);

  const signerVariableMetaMap = useMemo(() => {
    return signerVariables.reduce<
      Record<
        string,
        {
          type: SignerVariable["type"];
          dateFormat?: SignerVariable["dateFormat"];
        }
      >
    >((acc, variable) => {
      acc[variable.name] = {
        type: variable.type,
        dateFormat: variable.dateFormat,
      };
      return acc;
    }, {});
  }, [signerVariables]);

  const dynamicSignatureFormSchema = useMemo(
    () => buildSignatureFormSchema(signerVariables),
    [signerVariables],
  );

  // Check if the signature request has already been completed
  const requestingSignerSigned =
    requestingContact?.signatureRequestComplete === true ||
    requestingUser?.signatureRequestComplete === true;
  const [hasSigned, setHasSigned] = useState(requestingSignerSigned);
  const currentSignerComplete = hasSigned || requestingSignerSigned;

  const contactConnections = data.contactConnections;
  const userConnections = data.userConnections;
  const allSignersSigned =
    contactConnections.every(
      (connection) => connection.signatureRequestComplete === true,
    ) &&
    userConnections.every(
      (connection) => connection.signatureRequestComplete === true,
    );

  // Check if the request is expired
  const isExpired =
    dayjs().isAfter(dayjs(data.expirationDate)) && !requestingSignerSigned;

  const fileExtension = getFileNameParts(data.fileName).fileExtension;
  const isPdfDocument = fileExtension === "pdf";
  const isDocxDocument = fileExtension === "docx";
  const hasInteractivePortalFields =
    isPdfDocument && (data.signatureRequestFields?.length ?? 0) > 0;
  const [portalFieldsComplete, setPortalFieldsComplete] = useState(true);
  const hasSignedDocument = hasSigned && Boolean(signedFilePresignedUrlKey);

  const requestingSigner = requestingContact ?? requestingUser;
  const isWaitingForPriorSigners =
    !currentSignerComplete && requestingSigner?.pendingSigner !== true;
  const isSignerInteractionLocked =
    isExpired || currentSignerComplete || isWaitingForPriorSigners;

  // Check if the user can sign (not expired, not already signed, not waiting, and fields complete)
  const canSign =
    !isSignerInteractionLocked &&
    (!hasInteractivePortalFields || portalFieldsComplete);

  const handleSignedDocumentGeneratorChange = useCallback(
    (generator: (() => Promise<Blob>) | null) => {
      signedDocumentGeneratorRef.current = generator;
    },
    [],
  );

  const defaultFormValues = useMemo<Record<string, string>>(() => {
    const defaults: Record<string, string> = {};

    signerVariables.forEach((variable) => {
      defaults[variable.name] = "";
    });

    return defaults;
  }, [signerVariables]);

  const form = useForm<Record<string, string>>({
    resolver: signerVariables.length
      ? zodResolver(dynamicSignatureFormSchema)
      : undefined,
    defaultValues: defaultFormValues,
  });

  useEffect(() => {
    form.reset(defaultFormValues);
  }, [defaultFormValues, form]);

  const onSubmit = async (formData: Record<string, string>) => {
    setIsSigningDocument(true);
    try {
      const sanitizedFormData = Object.entries(formData).reduce<
        Record<string, string>
      >((acc, [key, value]) => {
        const trimmedValue = value.trim();
        if (trimmedValue) {
          const variableMeta = signerVariableMetaMap[key];
          if (variableMeta.type === "date") {
            const parsedDate = dayjs(trimmedValue);
            if (parsedDate.isValid()) {
              const formattedValue =
                variableMeta.dateFormat === "short"
                  ? parsedDate.format("M/D/YYYY")
                  : variableMeta.dateFormat === "long"
                    ? parsedDate.format("MMMM D, YYYY")
                    : parsedDate.format("MMM D, YYYY");
              acc[key] = formattedValue;
              return acc;
            }
          }
          acc[key] = trimmedValue;
        }
        return acc;
      }, {});
      const signatureRequestVariableValues =
        Object.keys(sanitizedFormData).length > 0
          ? sanitizedFormData
          : undefined;

      if (data.demoMode) {
        setHasSigned(true);
        return;
      }

      const signedDocumentBlob = signedDocumentGeneratorRef.current
        ? await signedDocumentGeneratorRef.current()
        : await (async () => {
            const requestData = {
              clientMethod: "get_object" as const,
              key: data.filePresignedUrlKey,
              signatureRequestPasscode: passcode,
            };
            const sourceDocumentResponse =
              scope === "matter"
                ? await getMatterSignatureRequestPublicFileUrlV2(requestData)
                : await getFirmSignatureRequestPublicFileUrlV2(requestData);
            const sourceDocument = await fetch(
              sourceDocumentResponse.data.presignedUrl,
            );
            return new Blob(
              [new Uint8Array(await sourceDocument.arrayBuffer())],
              {
                type:
                  sourceDocument.headers.get("content-type") ||
                  "application/pdf",
              },
            );
          })();

      const uploadRequestData = {
        clientMethod: "put_object" as const,
        fileName: data.fileName,
        key: data.filePresignedUrlKey,
        signatureRequestPasscode: passcode,
      };
      const uploadResponse =
        scope === "matter"
          ? await getMatterSignatureRequestPublicFileUrlV2(uploadRequestData)
          : await getFirmSignatureRequestPublicFileUrlV2(uploadRequestData);

      await fetch(uploadResponse.data.presignedUrl, {
        method: "PUT",
        headers: {
          "Content-Type": signedDocumentBlob.type || "application/pdf",
        },
        body: signedDocumentBlob,
      });

      const nextSignedFilePresignedUrlKey = uploadResponse.data.key;

      if (scope === "matter") {
        await fulfillMatterSignatureRequest.mutateAsync({
          connectionDateModified:
            requestingUser?.dateModified ??
            requestingContact?.dateModified ??
            data.dateModified,
          signatureRequestPasscode: passcode,
          signatureRequestDateModified: data.dateModified,
          variableValues: signatureRequestVariableValues ?? {},
          ...((data.signatureRequestFields?.length ?? 0) > 0
            ? {
                filePresignedUrlKey: nextSignedFilePresignedUrlKey,
                signatureRequestFilePresignedUrlKey: data.filePresignedUrlKey,
              }
            : {}),
        });
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.signatureRequestPortal, passcode],
        });
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.matterFileImages, data.filePresignedUrlKey],
        });
      } else {
        const basePayload = {
          filePresignedUrlKey: nextSignedFilePresignedUrlKey,
          signatureRequestDateModified: data.dateModified,
          signatureRequestFilePresignedUrlKey: data.filePresignedUrlKey,
          variableValues: signatureRequestVariableValues ?? {},
        };

        await fulfillFirmSignatureRequest.mutateAsync({
          ...basePayload,
          connectionDateModified:
            requestingUser?.dateModified ??
            requestingContact?.dateModified ??
            data.dateModified,
          signatureRequestPasscode: passcode,
        });
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.signatureRequestPortal, passcode],
        });
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.matterFileImages, data.filePresignedUrlKey],
        });
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.firmFiles],
        });
      }
      if (nextSignedFilePresignedUrlKey) {
        setSignedFilePresignedUrlKey(nextSignedFilePresignedUrlKey);
      }
      setHasSigned(true);
    } catch (error) {
      const normalizedErrorMessage = getSignatureRequestErrorMessage(error);
      if (
        normalizedErrorMessage.includes("signature request has been updated") ||
        normalizedErrorMessage.includes(
          "document has been updated by another signer",
        )
      ) {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.signatureRequestPortal, passcode],
        });
        await queryClient.refetchQueries({
          queryKey: [queryKeys.signatureRequestPortal, passcode],
        });
        toast.error(
          "The signature request changed. The latest version was reloaded. Please sign again.",
        );
        return;
      }

      if (normalizedErrorMessage.includes("signature request has expired")) {
        toast.error("This signature request has expired.");
        return;
      }

      if (normalizedErrorMessage.includes("waiting for another signer")) {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.signatureRequestPortal, passcode],
        });
        await queryClient.refetchQueries({
          queryKey: [queryKeys.signatureRequestPortal, passcode],
        });
        toast.error(
          "It isn't your turn to sign yet. Please wait for the prior signer.",
        );
        return;
      }

      console.error("Error signing document:", error);
      toast.error("Unable to sign document.");
    } finally {
      setIsSigningDocument(false);
    }
  };

  const getStatusBadge = () => {
    if (hasSigned) {
      return (
        <Badge variant="green">
          <CheckCircle className="mr-1 h-3 w-3" />
          Signed
        </Badge>
      );
    } else if (isExpired) {
      return (
        <Badge variant="destructive">
          <XCircle className="mr-1 h-3 w-3" />
          Expired
        </Badge>
      );
    } else {
      return <Badge variant="warning">Pending</Badge>;
    }
  };

  if (data.demoMode && data.initialDocUrl && data.demoLayoutFields) {
    return (
      <DemoSignatureRequestView
        fileName={data.fileName}
        initialDocUrl={data.initialDocUrl}
        layoutFields={data.demoLayoutFields}
        onBack={onBack}
      />
    );
  }

  return (
    <div className="flex h-svh w-full flex-col overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-slate-950 dark:to-indigo-950">
      {/* Main Document Area */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {/* Header */}
        <div className="dark:bg-card/80 border-b bg-white/80 backdrop-blur-sm">
          <div className="flex items-center justify-between p-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-muted-foreground"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <div className="flex items-center gap-2 lg:gap-4">
              <div className="text-center">
                <Typography
                  variant="heading"
                  size="sm"
                  className="text-sm lg:text-base"
                >
                  Signature request
                </Typography>
                <Typography
                  variant="default"
                  className="text-muted-foreground max-w-[150px] truncate text-xs lg:max-w-none lg:text-sm"
                >
                  {data.fileName}
                </Typography>
              </div>
              {getStatusBadge()}
              {hasInteractivePortalFields && !currentSignerComplete ? (
                <Button
                  type="button"
                  disabled={
                    isSignSubmitPending || !canSign || !portalFieldsComplete
                  }
                  title={isSignSubmitPending ? "Signing..." : "Sign document"}
                  onClick={() => {
                    void onSubmit({});
                  }}
                >
                  {isSignSubmitPending ? "Signing..." : "Sign document"}
                </Button>
              ) : null}
            </div>
          </div>
          {isWaitingForPriorSigners && (
            <div className="border-t border-yellow-200 bg-yellow-50 px-4 py-2 text-center">
              <Typography variant="default" className="text-xs text-yellow-800">
                It isn&apos;t your turn to sign yet. Pending prior signers to
                complete before you can sign.
              </Typography>
            </div>
          )}
        </div>

        {/* Document Viewer */}
        <div className="min-h-0 flex-1 overflow-hidden p-2 lg:p-4">
          <div className="bg-card mx-auto flex h-full min-h-0 w-full max-w-6xl overflow-hidden rounded-lg shadow-lg">
            {!data.demoMode &&
            !hasSignedDocument &&
            signatureRequestDocumentUrlQuery.isPending ? (
              <div className="flex h-full w-full items-center justify-center">
                <Typography className="text-muted-foreground text-sm">
                  Preparing document…
                </Typography>
              </div>
            ) : !data.demoMode &&
              !hasSignedDocument &&
              (signatureRequestDocumentUrlQuery.isError ||
                !signatureRequestDocumentUrl) ? (
              <div className="flex h-full w-full items-center justify-center px-6 text-center">
                <Typography className="text-muted-foreground text-sm">
                  Unable to load this signature request document.
                </Typography>
              </div>
            ) : hasSignedDocument ? (
              <div className="h-full min-h-0 w-full">
                <FileViewer
                  fileType={data.presignedUrlFileType}
                  file={{
                    fileName: `${data.fileName.replace(/\.(pdf|docx|doc)$/i, "")}-signed.pdf`,
                    presignedURLKey: signedFilePresignedUrlKey ?? "",
                  }}
                  presignedUrl={signatureRequestDocumentUrl}
                  showDownload
                  showTitle={false}
                  fileInWrapper={false}
                  unauthenticated={true}
                  imageClassName="h-full max-h-[5000px]"
                  viewerClassName="h-full"
                  wrapperClassName="h-full rounded-none shadow-none"
                />
              </div>
            ) : hasInteractivePortalFields ? (
              <div className="h-full min-h-0 w-full">
                <LiveSignatureRequestFieldsView
                  fileType={data.presignedUrlFileType}
                  fields={data.signatureRequestFields ?? []}
                  hideSignerFields={currentSignerComplete}
                  isSignerFieldsDisabled={isSignerInteractionLocked}
                  onSignedDocumentGeneratorChange={
                    handleSignedDocumentGeneratorChange
                  }
                  onCompletionChange={setPortalFieldsComplete}
                  presignedUrl={signatureRequestDocumentUrl}
                  presignedUrlKey={data.filePresignedUrlKey || ""}
                />
              </div>
            ) : isPdfDocument || isDocxDocument ? (
              <div className="h-full min-h-0 w-full">
                <FileViewer
                  fileType={data.presignedUrlFileType}
                  file={{
                    fileName: data.fileName,
                    presignedURLKey: data.filePresignedUrlKey || "",
                  }}
                  presignedUrl={signatureRequestDocumentUrl}
                  showDownload
                  showTitle={false}
                  fileInWrapper={false}
                  unauthenticated={true}
                  imageClassName="h-full max-h-[5000px]"
                  viewerClassName="h-full"
                  wrapperClassName="h-full rounded-none shadow-none"
                />
              </div>
            ) : (
              <div className="h-full min-h-0 w-full">
                <FileViewer
                  fileType={data.presignedUrlFileType}
                  file={{
                    fileName: data.fileName,
                    presignedURLKey: data.filePresignedUrlKey || "",
                  }}
                  presignedUrl={signatureRequestDocumentUrl}
                  showDownload
                  showTitle={false}
                  fileInWrapper={false}
                  unauthenticated={true}
                  imageClassName="h-full max-h-[5000px]"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {!hasInteractivePortalFields ? (
        <footer className="dark:bg-card/95 sticky bottom-0 z-30 shrink-0 border-t bg-white/95 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] backdrop-blur">
          <div className="grid gap-2 px-3 py-1.5 sm:px-4 sm:py-2 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center lg:px-5">
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <Typography variant="heading" size="sm" className="text-sm">
                  Sign
                </Typography>
                {getStatusBadge()}
              </div>
              {!currentSignerComplete ? (
                <Typography className="text-muted-foreground text-xs">
                  {signerVariables.length > 0
                    ? `${signerVariables.length} field${signerVariables.length === 1 ? "" : "s"} required`
                    : `Expires ${dayjs(data.expirationDate).format("MMM DD, YYYY")}`}
                </Typography>
              ) : null}

              {isExpired && (
                <div className="border-destructive/20 bg-destructive/5 rounded-md border px-2.5 py-1.5">
                  <Typography
                    variant="default"
                    className="text-destructive text-xs"
                  >
                    This signature request has expired. Please contact your
                    legal representative for assistance.
                  </Typography>
                </div>
              )}

              {hasSigned && !allSignersSigned && (
                <div className="rounded-md border border-yellow-200 bg-yellow-50 px-2.5 py-1.5">
                  <Typography
                    variant="default"
                    className="text-xs text-yellow-800"
                  >
                    Pending other signers to sign
                  </Typography>
                </div>
              )}
            </div>

            {!currentSignerComplete ? (
              <div className="flex items-center lg:justify-end">
                <Button
                  type="submit"
                  form="signature-request-sign-form"
                  disabled={isSignSubmitPending || !canSign}
                  className="w-full sm:w-auto"
                  title={isSignSubmitPending ? "Signing..." : "Sign document"}
                >
                  {isSignSubmitPending ? "Signing..." : "Sign document"}
                </Button>
              </div>
            ) : null}
          </div>

          {!currentSignerComplete ? (
            <Form {...form}>
              <form
                id="signature-request-sign-form"
                onSubmit={form.handleSubmit(onSubmit)}
              >
                <fieldset
                  disabled={isFulfillmentPending || isSignerInteractionLocked}
                  className="max-h-[14vh] overflow-auto px-3 py-1.5 sm:px-4 sm:py-2 lg:px-5"
                >
                  <div className="grid gap-2.5 md:grid-cols-2 xl:grid-cols-3">
                    {signerVariables.map((variable) => {
                      if (variable.type === "date") {
                        return (
                          <DateFormField
                            key={variable.name}
                            name={variable.name}
                            label={variable.label}
                            register={form.register(variable.name)}
                          />
                        );
                      }

                      if (variable.type === "signature") {
                        const signatureFieldValue =
                          form.watch(variable.name) || "";
                        const fieldError = form.formState.errors[variable.name];
                        const signatureFieldError =
                          typeof fieldError?.message === "string"
                            ? fieldError.message
                            : undefined;

                        return (
                          <div key={variable.name} className="space-y-1">
                            <Typography className="text-sm font-medium">
                              {variable.label}
                            </Typography>
                            <Input
                              type="text"
                              placeholder={`Type your ${variable.label.toLowerCase()}...`}
                              {...form.register(variable.name)}
                              className="text-sm"
                            />
                            {signatureFieldValue && (
                              <div className="bg-muted/50 mt-1.5 rounded border p-1.5">
                                <div
                                  className="text-foreground text-center"
                                  style={{
                                    fontFamily: "'Pacifico', cursive, serif",
                                    fontSize: "0.9rem",
                                    lineHeight: "1.2rem",
                                  }}
                                >
                                  {signatureFieldValue}
                                </div>
                              </div>
                            )}
                            {signatureFieldError && (
                              <Typography
                                variant="default"
                                className="text-destructive text-xs"
                              >
                                {signatureFieldError}
                              </Typography>
                            )}
                          </div>
                        );
                      }

                      return (
                        <TextFormField
                          key={variable.name}
                          label={variable.label}
                          register={form.register(variable.name)}
                          placeholder={`Enter your ${variable.label.toLowerCase()}`}
                        />
                      );
                    })}
                  </div>
                </fieldset>
              </form>
            </Form>
          ) : null}
        </footer>
      ) : null}
    </div>
  );
}
